import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Youtube, FileText, ExternalLink, Sparkles, BookOpen } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|\/embed\/)([\w-]{6,})/);
  return m ? m[1] : null;
}

function thumb(s) {
  if (s.thumbnail_url) return s.thumbnail_url;
  const id = youtubeId(s.youtube_url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

export default function Sermons() {
  const [tab, setTab] = useState("videos");
  const [ytVideos, setYtVideos] = useState([]);
  const [manualSermons, setManualSermons] = useState([]);
  const [active, setActive] = useState(null);
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    api.get("/youtube/videos?limit=15").then((r) => {
      setYtVideos(r.data);
      if (!active && r.data[0]) {
        setActive({
          type: "yt",
          title: r.data[0].title,
          speaker: r.data[0].speaker,
          date: (r.data[0].published || "").slice(0, 10),
          embed: r.data[0].embed_url,
        });
      }
    }).catch(() => {});
    api.get("/sermons").then((r) => setManualSermons(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build merged video list: YouTube uploads + admin-curated video sermons
  const adminVideos = manualSermons.filter(
    (s) => (s.sermon_type || "video") === "video" && (s.youtube_url || s.thumbnail_url)
  );
  const ytAsItems = ytVideos.map((v) => ({
    id: v.video_id,
    title: v.title,
    speaker: v.speaker,
    date: (v.published || "").slice(0, 10),
    thumbnail_url: v.thumbnail_url,
    embed_url: v.embed_url,
    source: "youtube",
  }));
  const adminAsItems = adminVideos.map((s) => ({
    id: s.id,
    title: s.title,
    speaker: s.speaker,
    date: s.date,
    description: s.description,
    thumbnail_url: thumb(s),
    embed_url: youtubeId(s.youtube_url)
      ? `https://www.youtube.com/embed/${youtubeId(s.youtube_url)}`
      : null,
    source: "curated",
  }));
  // De-duplicate: admin video with same youtube_url already covered by YT feed → drop dup
  const ytIds = new Set(ytVideos.map((v) => v.video_id));
  const dedupedAdmin = adminAsItems.filter((s) => {
    const id = youtubeId(s.embed_url || "");
    return !id || !ytIds.has(id);
  });
  const allVideos = [...ytAsItems, ...dedupedAdmin];

  const documents = manualSermons.filter((s) => s.sermon_type === "document");

  return (
    <div data-testid="sermons-page" className="bg-[#050505] text-white">
      <section className="py-32 md:py-40 px-6 md:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-4">Watch · Listen · Read · Grow</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-bold tracking-tighter leading-[0.9]">
            Sermons & <span className="text-[#ff5c00]">Media</span>
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mt-8">
            The Word changes lives. Catch up on the latest videos straight from
            our YouTube channel, or open the sermon notes for your own
            personal study.
          </p>
        </div>
      </section>

      {active && (
        <section className="py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 aspect-video rounded-sm overflow-hidden border border-white/10 bg-black">
              <iframe
                data-testid="active-sermon-iframe"
                key={active.embed}
                title={active.title}
                src={active.embed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            <div className="lg:col-span-4 space-y-3">
              <p className="font-mono-accent text-xs tracking-[0.25em] uppercase text-zinc-500">{active.date}</p>
              <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">{active.title}</h2>
              {active.speaker && <p className="text-zinc-400 text-sm">By {active.speaker}</p>}
              <a
                href="https://youtube.com/@rccg_ugycdubai"
                target="_blank"
                rel="noreferrer"
                data-testid="sermons-youtube-link"
                className="inline-flex items-center gap-2 mt-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-5 py-3 rounded-sm transition-colors"
              >
                <Youtube className="w-4 h-4" /> Subscribe on YouTube
              </a>
            </div>
          </div>
        </section>
      )}

      <section className="py-12 px-6 md:px-12 border-y border-white/5 bg-[#080808]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3">
          {[
            { id: "videos", label: `Videos · ${allVideos.length}` },
            { id: "documents", label: `Notes & Documents · ${documents.length}` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`sermons-tab-${t.id}`}
              className={`font-mono-accent text-xs tracking-[0.2em] uppercase px-4 py-2 rounded-sm border transition-colors ${
                tab === t.id
                  ? "border-[#ff5c00] text-white bg-[#ff5c00]/10"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
          {tab === "videos" && (
            <span className="ml-auto inline-flex items-center gap-2 text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500">
              <Sparkles className="w-3 h-3 text-[#ff5c00]" /> Synced from YouTube
            </span>
          )}
        </div>
      </section>

      {tab === "videos" && (
        <section className="py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            {allVideos.length === 0 ? (
              <p data-testid="no-sermons" className="text-zinc-500">No videos available right now — check back soon.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {allVideos.map((v, i) => (
                  <motion.button
                    key={`${v.source}-${v.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.04 }}
                    onClick={() => setActive({
                      type: v.source,
                      title: v.title,
                      speaker: v.speaker,
                      date: v.date,
                      embed: v.embed_url,
                    })}
                    data-testid={`sermon-${i}`}
                    className="group text-left bg-[#0f0f14] border border-white/10 rounded-sm overflow-hidden hover:border-[#ff5c00]/60 hover:-translate-y-1 transition-all"
                  >
                    <div className="aspect-video relative overflow-hidden bg-[#0a0a0d]">
                      {v.thumbnail_url && (
                        <img
                          src={v.thumbnail_url}
                          alt={v.title}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                        />
                      )}
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-[#ff5c00] flex items-center justify-center">
                          <Play className="w-5 h-5 text-white" fill="white" />
                        </div>
                      </div>
                      {v.source === "youtube" && (
                        <span className="absolute top-2 left-2 text-[10px] font-mono-accent uppercase tracking-widest px-2 py-0.5 bg-red-600/90 text-white rounded-sm">
                          YouTube
                        </span>
                      )}
                    </div>
                    <div className="p-5 space-y-2">
                      <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                        {v.date}{v.speaker ? ` · ${v.speaker}` : ""}
                      </p>
                      <h3 className="font-display text-base uppercase tracking-tight leading-tight line-clamp-2">{v.title}</h3>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {tab === "documents" && (
        <section className="py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            {documents.length === 0 ? (
              <p data-testid="no-documents" className="text-zinc-500">No sermon notes uploaded yet — check back soon.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {documents.map((d, i) => (
                  <motion.article
                    key={d.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    data-testid={`sermon-doc-${i}`}
                    className="group bg-[#0f0f14] border border-white/10 rounded-sm p-6 hover:border-[#ff5c00]/60 hover:-translate-y-1 transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <FileText className="w-7 h-7 text-[#ff5c00]" />
                      <span className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500">{d.date}</span>
                    </div>
                    <h3 className="font-display text-xl uppercase tracking-tight leading-tight mb-1">{d.title}</h3>
                    <p className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-500 mb-3">By {d.speaker}</p>
                    {d.description && <p className="text-zinc-400 text-sm mb-4 line-clamp-3">{d.description}</p>}
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
                      {d.text_content && (
                        <button
                          onClick={() => setDoc(d)}
                          data-testid={`sermon-doc-read-${i}`}
                          className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-[11px] tracking-widest uppercase px-3 py-2 rounded-sm transition-colors"
                        >
                          <BookOpen className="w-3.5 h-3.5" /> Read Notes
                        </button>
                      )}
                      {d.document_url && (
                        <a
                          href={d.document_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`sermon-doc-link-${i}`}
                          className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff5c00] hover:text-[#ff5c00] text-zinc-300 font-mono-accent text-[11px] tracking-widest uppercase px-3 py-2 rounded-sm"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Open Document
                        </a>
                      )}
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Document reader dialog */}
      <Dialog open={!!doc} onOpenChange={(o) => !o && setDoc(null)}>
        <DialogContent data-testid="sermon-doc-dialog" className="bg-[#0a0a0d] border border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          {doc && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl md:text-3xl uppercase tracking-tight">{doc.title}</DialogTitle>
                <DialogDescription className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-400">
                  {doc.date} · {doc.speaker}
                </DialogDescription>
              </DialogHeader>
              <article className="whitespace-pre-wrap leading-relaxed text-zinc-200 pt-3">
                {doc.text_content}
              </article>
              {doc.document_url && (
                <a
                  href={doc.document_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 mt-4 border border-white/15 hover:border-[#ff5c00] hover:text-[#ff5c00] text-zinc-300 font-mono-accent text-xs tracking-widest uppercase px-4 py-2 rounded-sm w-fit"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open Full Document
                </a>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
